<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue';

export interface StreamStats {
  fps: number;
  bitrateKbps: number;
  packetsLost: number;
  jitterMs: number;
  freezeCount: number;
  rttMs: number;
  candidateType: string;
}

const props = defineProps<{
  videoStream?: MediaStream | null;
  videoUrl?: string | null;
  audioStream?: MediaStream | null;
  isLocal: boolean;
  streamerName: string;
  stats?: StreamStats;
  viewers?: { current: number; max: number };
  isFull?: boolean;
  fullInfo?: { current: number; max: number; streamerName: string } | null;
  whepStatus?: 'idle' | 'connecting' | 'playing' | 'full' | 'error';
  errorMessage?: string | null;
}>();

defineEmits<{
  (e: 'retry'): void;
}>();

const videoRef = ref<HTMLVideoElement | null>(null);
const audioRef = ref<HTMLAudioElement | null>(null);
const containerRef = ref<HTMLDivElement | null>(null);

const isMuted = ref(props.isLocal);
const volume = ref(1);
const isFullscreen = ref(false);
const showControls = ref(true);
const videoDimensions = ref({ width: 0, height: 0 });
const audioBlocked = ref(false);
const showStatsHud = ref(false);

let controlsTimeout: number | undefined;

function resetControlsTimer() {
  showControls.value = true;
  if (controlsTimeout) clearTimeout(controlsTimeout);
  controlsTimeout = window.setTimeout(() => {
    if (isFullscreen.value) {
      showControls.value = false;
    }
  }, 3000);
}

// ── Gerenciamento Estável de Áudio (Elemento <audio> Exclusivo) ───────────────
let lastAudioTrackId = '';

watch(
  () => props.audioStream,
  (newStream) => {
    if (!audioRef.value || props.isLocal) return;

    const stream = (newStream && typeof newStream === 'object' && 'value' in newStream)
      ? (newStream as any).value
      : newStream;

    if (stream && typeof stream.getAudioTracks === 'function' && stream.getAudioTracks().length > 0) {
      const track = stream.getAudioTracks()[0];
      if (track.id !== lastAudioTrackId) {
        lastAudioTrackId = track.id;
        track.enabled = !isMuted.value;
        audioRef.value.srcObject = stream;
        tryPlayAudio();
      }
    } else {
      audioRef.value.srcObject = null;
      lastAudioTrackId = '';
    }
  },
  { immediate: true }
);

function tryPlayAudio() {
  if (!audioRef.value || props.isLocal) return;
  audioRef.value.muted = isMuted.value;
  audioRef.value.volume = volume.value;

  const promise = audioRef.value.play();
  if (promise !== undefined) {
    promise
      .then(() => {
        audioBlocked.value = false;
      })
      .catch((err) => {
        if (err.name === 'NotAllowedError') {
          console.warn('[StreamPlayer] Reprodução automática de áudio bloqueada pelo navegador.');
          audioBlocked.value = true;
        }
      });
  }
}

function unlockAudio() {
  if (audioRef.value) {
    audioRef.value.muted = false;
    isMuted.value = false;
    if (volume.value === 0) volume.value = 1;
    audioRef.value.volume = volume.value;
    audioRef.value.play().then(() => {
      audioBlocked.value = false;
    }).catch(() => {});
  }
}

// ── Gerenciamento Estável de Vídeo (Sempre Muted) ─────────────────────────────
let lastVideoTrackId = '';

watch(
  [() => props.videoStream, () => props.videoUrl],
  ([newStream, newUrl]) => {
    if (!videoRef.value) return;

    const stream = (newStream && typeof newStream === 'object' && 'value' in newStream)
      ? (newStream as any).value
      : newStream;

    if (stream && typeof stream.getVideoTracks === 'function' && stream.getVideoTracks().length > 0) {
      const track = stream.getVideoTracks()[0];
      if (track.id !== lastVideoTrackId) {
        lastVideoTrackId = track.id;
        videoRef.value.src = '';
        videoRef.value.srcObject = stream;
        videoRef.value.muted = true; // Vídeo SEMPRE mutado para evitar conflito com <audio>
        videoRef.value.play().catch(() => {});

        track.onunmute = () => {
          if (videoRef.value) {
            videoDimensions.value = {
              width: videoRef.value.videoWidth,
              height: videoRef.value.videoHeight,
            };
          }
        };
      }
    } else if (newUrl) {
      lastVideoTrackId = '';
      videoRef.value.srcObject = null;
      videoRef.value.src = newUrl;
      videoRef.value.muted = true;
      videoRef.value.play().catch(() => {});
    } else {
      videoRef.value.srcObject = null;
      videoRef.value.src = '';
      lastVideoTrackId = '';
    }
  },
  { immediate: true }
);

function toggleMute() {
  isMuted.value = !isMuted.value;
  if (audioRef.value) {
    audioRef.value.muted = isMuted.value;
    if (!isMuted.value) {
      unlockAudio();
    }
  }
}

function onVolumeChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const val = parseFloat(target.value);
  volume.value = val;
  isMuted.value = val === 0;
  if (audioRef.value) {
    audioRef.value.volume = val;
    audioRef.value.muted = isMuted.value;
    if (!isMuted.value) {
      unlockAudio();
    }
  }
}

function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value;
  if (containerRef.value) {
    if (isFullscreen.value) {
      try {
        if (containerRef.value.requestFullscreen) {
          containerRef.value.requestFullscreen().catch(() => {});
        }
      } catch (e) {}
    } else {
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
      } catch (e) {}
    }
  }
}

function onLoadedMetadata() {
  if (videoRef.value) {
    videoDimensions.value = {
      width: videoRef.value.videoWidth,
      height: videoRef.value.videoHeight,
    };
  }
}

onMounted(() => {
  if (videoRef.value) {
    if (props.videoStream && props.videoStream.getVideoTracks().length > 0) {
      lastVideoTrackId = props.videoStream.getVideoTracks()[0].id;
      videoRef.value.srcObject = props.videoStream;
      videoRef.value.muted = true;
      videoRef.value.play().catch(() => {});
    } else if (props.videoUrl) {
      videoRef.value.src = props.videoUrl;
      videoRef.value.muted = true;
      videoRef.value.play().catch(() => {});
    }
  }

  if (audioRef.value && props.audioStream) {
    audioRef.value.srcObject = props.audioStream;
    tryPlayAudio();
  }
});

onUnmounted(() => {
  if (controlsTimeout) clearTimeout(controlsTimeout);
});
</script>

<template>
  <div
    ref="containerRef"
    class="bg-black flex items-center justify-center overflow-hidden select-none group"
    :class="isFullscreen ? 'fixed inset-0 w-screen h-screen z-[999999] rounded-none' : 'relative w-full h-full rounded-lg'"
    @mousemove="resetControlsTimer"
    @mouseleave="showControls = false"
    @dblclick="toggleFullscreen"
    @click="unlockAudio"
  >
    <!-- Elemento de Áudio Oculto (Pipeline Dedicado e Isolado) -->
    <audio
      ref="audioRef"
      autoplay
      playsinline
      :muted="isLocal || isMuted"
    ></audio>

    <!-- Elemento de Vídeo WebRTC (Sempre Muted para Máxima Fluidez) -->
    <video
      ref="videoRef"
      class="w-full h-full object-contain pointer-events-none"
      autoplay
      playsinline
      muted
      @loadedmetadata="onLoadedMetadata"
    ></video>

    <!-- Overlay: Sala Cheia (Limite de Upload do Streamer) -->
    <div
      v-if="isFull"
      class="absolute inset-0 flex flex-col items-center justify-center p-6 bg-black/90 z-30 select-none text-center animate-fade-in"
    >
      <div class="w-16 h-16 rounded-full bg-[#f23f43]/15 border border-[#f23f43]/30 flex items-center justify-center text-[#f23f43] mb-4">
        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 class="text-lg font-bold text-white">Transmissão em Capacidade Máxima</h3>
      <p class="text-xs text-[#949ba4] max-w-sm mt-1.5 leading-relaxed">
        {{ fullInfo?.streamerName || streamerName }} atingiu o limite de espectadores suportado pelo upload da conexão doméstica
        <strong class="text-white">({{ fullInfo?.current || viewers?.current || 2 }}/{{ fullInfo?.max || viewers?.max || 2 }})</strong>.
      </p>
      <div class="mt-4 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] text-[#dbdee1] flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-[#f0b232] animate-pulse"></span>
        <span>Aguardando liberação de vaga...</span>
      </div>
    </div>

    <!-- Overlay: Erro na Conexão WHEP -->
    <div
      v-else-if="whepStatus === 'error' && !isLocal"
      class="absolute inset-0 flex flex-col items-center justify-center p-6 bg-black/90 z-30 select-none text-center animate-fade-in"
    >
      <div class="w-14 h-14 rounded-full bg-[#f23f43]/15 border border-[#f23f43]/30 flex items-center justify-center text-[#f23f43] mb-3">
        <svg class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 class="text-sm font-bold text-white">Falha ao Conectar à Transmissão</h3>
      <p class="text-xs text-[#949ba4] max-w-sm mt-1.5 leading-relaxed">
        {{ errorMessage || 'Não foi possível sincronizar o vídeo com o transmissor.' }}
      </p>
      <button
        @click="$emit('retry')"
        class="mt-4 px-4 py-1.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] text-white text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-md"
      >
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Tentar Novamente</span>
      </button>
    </div>

    <!-- Overlay: Aguardando Transmissão -->
    <div
      v-else-if="!videoStream && !videoUrl && !isLocal"
      class="absolute inset-0 flex flex-col items-center justify-center p-4 bg-black/85 z-10 select-none animate-fade-in"
    >
      <div class="w-12 h-12 rounded-full bg-[#5865F2]/20 border border-[#5865F2]/30 flex items-center justify-center text-[#5865F2] mb-3">
        <svg class="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <h3 class="text-xs font-semibold text-white">Sincronizando transmissão WHEP...</h3>
      <p class="text-[11px] text-[#949ba4] mt-1">{{ streamerName }} está ao vivo</p>
      <button
        @click="$emit('retry')"
        class="mt-3.5 px-3.5 py-1.5 rounded-lg bg-[#5865F2]/20 hover:bg-[#5865F2] border border-[#5865F2]/40 text-white text-[11px] font-semibold transition cursor-pointer active:scale-95 flex items-center gap-1.5 shadow-sm"
      >
        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Sincronizar Agora</span>
      </button>
    </div>

    <!-- Overlay: Clique para Ativar Áudio (Autoplay Bloqueado) -->
    <div
      v-if="!isLocal && audioBlocked"
      class="absolute inset-0 flex items-center justify-center z-25 pointer-events-none"
    >
      <button
        @click.stop="unlockAudio"
        class="pointer-events-auto flex flex-col items-center gap-2 px-6 py-4 rounded-2xl bg-black/80 backdrop-blur-md border border-[#5865F2]/60 text-white shadow-2xl transition hover:scale-105 cursor-pointer"
      >
        <svg class="w-8 h-8 text-[#5865F2] animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        </svg>
        <span class="text-sm font-bold">Clique para ativar o áudio do jogo</span>
        <span class="text-[11px] text-white/60">O navegador pausou o som automático</span>
      </button>
    </div>

    <!-- Top Left: Badge Ao Vivo & Info -->
    <div
      class="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded bg-[#111214]/80 backdrop-blur-xs border border-white/10 transition-opacity duration-200"
      :class="{ 'opacity-0': !showControls && isFullscreen }"
    >
      <span class="flex h-2 w-2 relative">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f23f43] opacity-75"></span>
        <span class="relative inline-flex rounded-full h-2 w-2 bg-[#f23f43]"></span>
      </span>
      <span class="text-[10px] font-bold tracking-wider text-[#f23f43] uppercase">AO VIVO</span>
      <span class="text-white/20 text-xs">|</span>
      <span class="text-xs font-medium text-white truncate max-w-[130px]">
        {{ isLocal ? 'Você (Preview)' : streamerName }}
      </span>

      <!-- Badge de FPS Real -->
      <button
        @click.stop="showStatsHud = !showStatsHud"
        class="text-[10px] text-[#23a55a] bg-[#23a55a]/15 hover:bg-[#23a55a]/30 px-1.5 py-0.5 rounded font-bold border border-[#23a55a]/30 transition cursor-pointer flex items-center gap-1"
        :title="showStatsHud ? 'Ocultar Estatísticas' : 'Ver Estatísticas de Fluidez e FPS'"
      >
        <span class="w-1.5 h-1.5 rounded-full bg-[#23a55a]"></span>
        <span>{{ stats?.fps || 60 }} FPS</span>
      </button>

      <!-- Badge de Espectadores / Capacidade -->
      <span v-if="viewers" class="text-[10px] text-[#dbdee1] bg-white/10 px-1.5 py-0.5 rounded font-mono">
        👥 {{ viewers.current }}/{{ viewers.max }}
      </span>
    </div>

    <!-- Modal HUD de Diagnóstico Real -->
    <div
      v-if="showStatsHud"
      class="absolute top-12 left-3 z-40 bg-[#111214]/95 backdrop-blur-md border border-white/15 rounded-xl p-3.5 shadow-2xl text-[11px] text-[#dbdee1] w-64 space-y-2 animate-fade-in select-none"
    >
      <div class="flex items-center justify-between pb-1.5 border-b border-white/10">
        <span class="font-bold text-white flex items-center gap-1.5">
          <span>⚡</span>
          <span>Métricas de Fluidez WHEP</span>
        </span>
        <button
          @click="showStatsHud = false"
          class="text-[#949ba4] hover:text-white cursor-pointer text-xs"
        >✕</button>
      </div>

      <div class="space-y-1 font-mono text-[10.5px]">
        <div class="flex justify-between">
          <span class="text-[#949ba4]">Protocolo:</span>
          <span class="text-[#23a55a] font-bold">WHEP (MediaMTX)</span>
        </div>
        <div class="flex justify-between">
          <span class="text-[#949ba4]">Taxa de Quadros:</span>
          <span class="text-white font-bold">{{ stats?.fps || 60 }} FPS</span>
        </div>
        <div class="flex justify-between">
          <span class="text-[#949ba4]">Bitrate Recebido:</span>
          <span class="text-white">{{ stats?.bitrateKbps || 6000 }} Kbps</span>
        </div>
        <div class="flex justify-between">
          <span class="text-[#949ba4]">Quadros Perdidos:</span>
          <span :class="(stats?.packetsLost || 0) > 10 ? 'text-[#f0b232]' : 'text-[#23a55a]'">
            {{ stats?.packetsLost || 0 }} pacotes
          </span>
        </div>
        <div class="flex justify-between">
          <span class="text-[#949ba4]">Jitter / RTT:</span>
          <span class="text-white">{{ stats?.jitterMs || 0 }}ms / {{ stats?.rttMs || 0 }}ms</span>
        </div>
        <div class="flex justify-between">
          <span class="text-[#949ba4]">Congelamentos:</span>
          <span :class="(stats?.freezeCount || 0) > 0 ? 'text-[#f23f43]' : 'text-[#23a55a]'">
            {{ stats?.freezeCount || 0 }} detectados
          </span>
        </div>
      </div>
    </div>

    <!-- Bottom Controls Overlay -->
    <div
      class="absolute bottom-3 left-3 right-3 flex items-center justify-between px-3 py-1.5 rounded-lg bg-[#111214]/85 backdrop-blur-xs border border-white/10 transition-opacity duration-200"
      :class="{ 'opacity-0 pointer-events-none': !showControls && isFullscreen }"
    >
      <!-- Volume & Mute -->
      <div class="flex items-center gap-2">
        <button
          @click.stop="toggleMute"
          class="p-1 rounded hover:bg-white/10 text-[#dbdee1] hover:text-white transition cursor-pointer"
          :title="isMuted ? 'Desmutar' : 'Mutar'"
        >
          <svg v-if="isMuted || volume === 0" class="w-4 h-4 text-[#f23f43]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
        </button>

        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          :value="isMuted ? 0 : volume"
          @input.stop="onVolumeChange"
          class="w-20 h-1 bg-white/20 rounded appearance-none cursor-pointer accent-[#5865F2]"
        />
        <span class="text-[11px] text-[#949ba4] w-7">{{ Math.round(isMuted ? 0 : volume * 100) }}%</span>

        <span
          class="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase transition"
          :class="isMuted ? 'bg-[#f23f43]/20 text-[#f23f43]' : 'bg-[#23a55a]/20 text-[#23a55a]'"
        >
          {{ isMuted ? 'Áudio Mutado' : 'Som HD Isolado' }}
        </span>
      </div>

      <!-- Right: Fullscreen -->
      <div class="flex items-center gap-2">
        <button
          @click.stop="toggleFullscreen"
          class="p-1 rounded hover:bg-white/10 text-[#dbdee1] hover:text-white transition cursor-pointer"
          :title="isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'"
        >
          <svg v-if="isFullscreen" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
